import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSearch, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Field, inputCls } from "@/components/LegsEditor";
import { useApi, errMsg } from "@/lib/api";
import { code } from "@/components/TrackingBits";

const STALE_MS = 6 * 60 * 1000;
const FBO_KEYS = [["name", "FBO name"], ["address", "Full address"], ["phone", "Phone"]];
const OP_KEYS = [["operator_trip_id", "Operator trip ID"], ["tail_number", "Tail number"], ["operator_name", "Operator name"], ["operator_phone", "Operator phone"], ["operator_address", "Operator address"]];
const pick = (found, current) => found || current || "";
const fbo = (found = {}, cur = {}) => Object.fromEntries(FBO_KEYS.map(([k]) => [k, pick(found[k], cur[k])]));

export const readState = (t) => {
  const r = t.itinerary_read;
  if (!r) return null;
  if (r.status === "running" && Date.now() - new Date(r.started_at).getTime() > STALE_MS) return "failed";
  return r.status;
};

export function ItineraryStatus({ t, onSaved, onReview }) {
  const api = useApi();
  const state = readState(t);
  const again = async () => {
    try { onSaved((await api.post(`/tracking/${t.id}/itinerary/read`)).data); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!t.trip_sheet) return null;
  if (state === "running") return <span className="inline-flex items-center gap-2 text-xs text-zinc-200 py-3" data-testid="itinerary-reading"><Loader2 size={13} className="animate-spin text-[#C6A55C]" /> Reading the itinerary…</span>;
  if (state === "done" && !t.itinerary_read.applied) return <button type="button" className="frame-btn gold" onClick={onReview} data-testid="itinerary-review-btn"><FileSearch size={13} /> Review itinerary details</button>;
  return (
    <div className="flex flex-col gap-1">
      <button type="button" className="frame-btn" onClick={again} data-testid="itinerary-read-btn"><FileSearch size={13} /> {state === "done" ? "Read itinerary again" : state === "failed" ? "Try reading again" : "Read itinerary"}</button>
      {state === "failed" && <span className="text-[0.72rem] text-rose-300" data-testid="itinerary-read-failed">The itinerary couldn't be read. Try again or enter the details by hand.</span>}
    </div>
  );
}

function Box({ label, value, onChange, hit, testId, wide }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <Field label={label}>
        <Input className={`${inputCls} ${hit ? "!border-[#C6A55C]/70" : ""}`} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
      </Field>
    </div>
  );
}

function FboFields({ title, value, found = {}, onChange, testId }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <p className="eyebrow sm:col-span-2 !mb-0">{title}</p>
      {FBO_KEYS.map(([k, label]) => <Box key={k} label={label} value={value[k]} hit={!!found[k]} wide={k === "address"} testId={`${testId}-${k}`} onChange={(v) => onChange({ ...value, [k]: v })} />)}
    </div>
  );
}

function initForm(t) {
  const r = t.itinerary_read?.result || { legs: [] };
  return {
    aircraft: Object.fromEntries(OP_KEYS.map(([k]) => [k, pick(r[k], t.aircraft[k])])),
    legs: t.legs.map((lg, i) => {
      const x = r.legs[i] || {};
      return { dep_fbo: fbo(x.dep_fbo, lg.dep_fbo), arr_fbo: fbo(x.arr_fbo, lg.arr_fbo), arr_time: pick(x.arr_time, lg.arr_time) };
    }),
  };
}

export function ItineraryReviewDialog({ open, t, onClose, onSaved }) {
  const api = useApi();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initForm(t)); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open || !f) return null;
  const r = t.itinerary_read?.result || { legs: [] };
  const setLeg = (i, k, v) => setF((s) => ({ ...s, legs: s.legs.map((lg, j) => (j === i ? { ...lg, [k]: v } : lg)) }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      onSaved((await api.post(`/tracking/${t.id}/itinerary/apply`, f)).data);
      toast.success("Itinerary details saved. Calendar invites are on their way.");
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#111319] border-white/10 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="itinerary-review-dialog">
        <DialogHeader>
          <p className="eyebrow">{t.trip_ref}</p>
          <DialogTitle className="font-serif-display text-3xl font-normal text-white">Check the itinerary details</DialogTitle>
          <p className="text-xs text-zinc-400 pt-1">Boxes with a gold edge were read from the itinerary. Fix anything that's wrong, then save.</p>
        </DialogHeader>
        <form onSubmit={save} className="space-y-6 mt-2">
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="itinerary-operator">
            <p className="eyebrow sm:col-span-2 !mb-0">Operator & aircraft</p>
            {OP_KEYS.map(([k, label]) => <Box key={k} label={label} value={f.aircraft[k]} hit={!!r[k]} wide={k === "operator_address"} testId={`itin-${k}`}
              onChange={(v) => setF((s) => ({ ...s, aircraft: { ...s.aircraft, [k]: v } }))} />)}
          </section>
          {f.legs.map((lg, i) => (
            <section key={i} className="space-y-4 border-t border-white/[0.08] pt-5" data-testid={`itinerary-leg-${i}`}>
              <p className="font-serif-display text-xl text-white">Leg {i + 1} · {code(t.legs[i].from_airport)} → {code(t.legs[i].to_airport)}</p>
              <FboFields title="Departure FBO" value={lg.dep_fbo} found={r.legs[i]?.dep_fbo} testId={`itin-leg-${i}-dep`} onChange={(v) => setLeg(i, "dep_fbo", v)} />
              <FboFields title="Arrival FBO" value={lg.arr_fbo} found={r.legs[i]?.arr_fbo} testId={`itin-leg-${i}-arr`} onChange={(v) => setLeg(i, "arr_fbo", v)} />
              <div className="sm:w-1/2">
                <Field label={`Scheduled arrival (local at ${code(t.legs[i].to_airport)})`}>
                  <Input type="time" className={`${inputCls} ${r.legs[i]?.arr_time ? "!border-[#C6A55C]/70" : ""}`} value={lg.arr_time} onChange={(e) => setLeg(i, "arr_time", e.target.value)} data-testid={`itin-leg-${i}-arr-time`} />
                </Field>
              </div>
            </section>
          ))}
          <div className="flex justify-end gap-3">
            <button type="button" className="frame-btn" onClick={onClose} data-testid="itinerary-cancel-btn">Not now</button>
            <button type="submit" className="frame-btn gold" disabled={busy} data-testid="itinerary-save-btn">{busy ? "Saving…" : "Save details"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
